import { ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight, CircleDollarSign, CloudSun, RotateCcw, Save, Trash2, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Notice } from "../../components/Notice";
import { catalogById } from "../../data/catalog";
import type { BalconyProfile, CatalogItem, DesignItem, PlanTemplate, Zone } from "../../data/model";
import { lightLabels, styles } from "../../data/taxonomy";
import { aisleWidthCm, areaM2, careMinutes, estimateTotal, plantCount } from "../../lib/calculations";
import { formatArea, formatCompactMoney, formatMoney } from "../../lib/format";
import { CatalogPanel } from "./CatalogPanel";
import { PlanCanvas, zoneLabel } from "./PlanCanvas";

interface Props {
  profile: BalconyProfile;
  design: DesignItem[];
  template: PlanTemplate;
  packageId: string;
  saved: boolean;
  lastRemoved: DesignItem | null;
  onDesignChange: (design: DesignItem[]) => void;
  onPackageChange: (id: string) => void;
  onSave: () => void;
  onUndo: () => void;
  onReview: () => void;
  onBack: () => void;
  onReset: () => void;
}

export function Workspace({
  profile,
  design,
  template,
  packageId,
  saved,
  lastRemoved,
  onDesignChange,
  onPackageChange,
  onSave,
  onUndo,
  onReview,
  onBack,
  onReset,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(design[0]?.instanceId ?? null);
  const [replaceMode, setReplaceMode] = useState(false);
  const [notice, setNotice] = useState("");
  const [mobileTab, setMobileTab] = useState<"plan" | "catalog" | "summary">("plan");

  const selected = design.find((item) => item.instanceId === selectedId) ?? null;
  const selectedRecord = selected ? catalogById.get(selected.refId) : null;
  const total = estimateTotal(design, packageId);
  const delta = profile.budget - total;
  const aisle = aisleWidthCm(profile, design);
  const style = styles.find((entry) => entry.id === profile.styleId);

  const compatibilityCount = useMemo(
    () => new Set(design.filter((item) => item.type === "plant").map((item) => item.refId)).size,
    [design],
  );

  const addOrReplace = (record: CatalogItem) => {
    setNotice("");
    if (replaceMode && selected) {
      onDesignChange(
        design.map((item) =>
          item.instanceId === selected.instanceId
            ? { ...item, refId: record.id, type: record.type, qty: 1 }
            : item,
        ),
      );
      setReplaceMode(false);
      setNotice(`Đã thay bằng ${record.name}.`);
      setMobileTab("plan");
      return;
    }
    if (design.length >= 12) {
      setNotice("Bản phác đã có 12 dòng hạng mục — bớt một món trước khi thêm.");
      return;
    }
    const zone: Zone = record.type === "furniture" ? "right" : record.type === "plant" ? "left" : "center";
    const next: DesignItem = {
      instanceId: `custom-${record.id}-${design.length + 1}`,
      refId: record.id,
      type: record.type,
      qty: 1,
      zone,
      x: zone === "left" ? 22 : zone === "center" ? 52 : 80,
      y: 35 + (design.length % 3) * 14,
    };
    onDesignChange([...design, next]);
    setSelectedId(next.instanceId);
    setNotice(`Đã thêm ${record.name}.`);
    setMobileTab("plan");
  };

  const move = (zone: Zone) => {
    if (!selected) return;
    const x = zone === "left" ? 22 : zone === "center" ? 52 : 80;
    onDesignChange(design.map((item) => (item.instanceId === selected.instanceId ? { ...item, zone, x } : item)));
    setNotice(`Đã chuyển ${selectedRecord?.name} sang vùng ${zoneLabel(zone)}.`);
  };

  const remove = () => {
    if (!selected) return;
    onDesignChange(design.filter((item) => item.instanceId !== selected.instanceId));
    setNotice(`Đã bỏ ${selectedRecord?.name}.`);
    setSelectedId(null);
    setReplaceMode(false);
  };

  const removeExpensiveNonessential = () => {
    const candidates = design
      .map((item) => ({ item, record: catalogById.get(item.refId) }))
      .filter(({ record }) => record && (record.type === "furniture" || record.type === "accessory"))
      .filter(({ record }) => record?.type !== "accessory" || !record.essential)
      .sort((a, b) => (b.record?.unitPrice ?? 0) - (a.record?.unitPrice ?? 0));
    const target = candidates[0];
    if (!target) {
      setNotice("Không còn hạng mục không thiết yếu để bỏ tự động.");
      return;
    }
    onDesignChange(design.filter((item) => item.instanceId !== target.item.instanceId));
    setNotice(`Đã bỏ ${target.record?.name}, giảm ${formatMoney((target.record?.unitPrice ?? 0) * target.item.qty)}.`);
    if (selectedId === target.item.instanceId) setSelectedId(null);
  };

  return (
    <div className="workspace">
      <div className="workspace-top">
        <div>
          <button className="back-link" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" /> Sửa điều kiện</button>
          <span className="eyebrow">Phương án 01 · mô phỏng</span>
          <h1>{template.name}</h1>
          <p>{template.description}</p>
        </div>
        <div className="workspace-top-actions">
          <button className="button button-ghost" type="button" onClick={onSave}><Save aria-hidden="true" /> {saved ? "Đã cất" : "Cất bản phác"}</button>
          <button className="icon-button" type="button" onClick={onReset} aria-label="Reset demo"><RotateCcw aria-hidden="true" /></button>
        </div>
      </div>

      <div className="match-reasons">
        <span><CloudSun aria-hidden="true" /><strong>{lightLabels[profile.light]}</strong> hợp {compatibilityCount} nhóm cây đang dùng</span>
        <span><ChevronRight aria-hidden="true" /><strong>Lối thao tác ~{aisle} cm</strong> {aisle < 50 ? "· cần nới" : "· đủ dùng"}</span>
        <span><Check aria-hidden="true" /><strong>{careMinutes(design)} phút/tuần</strong> theo dữ liệu cây</span>
      </div>

      {delta < 0 && (
        <Notice
          tone="warning"
          title={`Phương án đang vượt ${formatMoney(Math.abs(delta))}.`}
          actions={
            <>
              <button className="text-button" type="button" onClick={removeExpensiveNonessential}>Bỏ món không thiết yếu đắt nhất</button>
              {packageId !== "sketch" && <button className="text-button" type="button" onClick={() => onPackageChange("sketch")}>Đổi về gói Bản Phác</button>}
            </>
          }
        >
          <p>Bạn vẫn có thể xem đầy đủ dự toán hoặc dùng một trong hai cách giảm chi phí.</p>
        </Notice>
      )}

      {notice && <div className="workspace-status" role="status">{notice}</div>}

      <div className="mobile-workspace-tabs" role="tablist" aria-label="Chế độ workspace">
        {([["plan", "Bố trí"], ["catalog", "Thêm đồ"], ["summary", "Tổng"]] as const).map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={mobileTab === value} onClick={() => setMobileTab(value)}>{label}</button>
        ))}
      </div>

      <div className="workspace-grid">
        <aside className={`workspace-sidebar ${mobileTab === "catalog" ? "mobile-active" : ""}`}>
          <CatalogPanel profile={profile} design={design} replaceMode={replaceMode} onAdd={addOrReplace} />
        </aside>
        <section className={`workspace-main ${mobileTab === "plan" ? "mobile-active" : ""}`}>
          <div className="canvas-heading">
            <div><span className="eyebrow">{profile.widthCm} × {profile.depthCm} cm · {formatArea(areaM2(profile.widthCm, profile.depthCm))} m²</span><h2>Bản phác nhìn từ trên</h2></div>
            <span>{style?.shortName}</span>
          </div>
          <PlanCanvas profile={profile} design={design} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setReplaceMode(false); }} />
          {selected && selectedRecord ? (
            <div className="item-inspector">
              <div>
                <span className="eyebrow">Đang chọn · vùng {zoneLabel(selected.zone)}</span>
                <h3>{selectedRecord.name} {selected.qty > 1 ? `×${selected.qty}` : ""}</h3>
                <p>{selectedRecord.description}</p>
              </div>
              <div className="inspector-actions">
                <div className="zone-controls" aria-label="Chuyển vùng">
                  <button type="button" onClick={() => move("left")} disabled={selected.zone === "left"}><ChevronLeft aria-hidden="true" /> Trái</button>
                  <button type="button" onClick={() => move("center")} disabled={selected.zone === "center"}>Giữa</button>
                  <button type="button" onClick={() => move("right")} disabled={selected.zone === "right"}>Phải <ChevronRight aria-hidden="true" /></button>
                </div>
                <button className={replaceMode ? "active" : ""} type="button" onClick={() => { setReplaceMode((value) => !value); setMobileTab("catalog"); }}>Thay món</button>
                <button className="danger-button" type="button" onClick={remove}><Trash2 aria-hidden="true" /> Bỏ</button>
              </div>
            </div>
          ) : (
            <div className="item-inspector empty"><p>Chọn một món trên bản phác để chuyển vùng, thay hoặc bỏ.</p></div>
          )}
        </section>
        <aside className={`workspace-summary ${mobileTab === "summary" ? "mobile-active" : ""}`}>
          <div className="live-summary">
            <span className="eyebrow">Cập nhật trực tiếp</span>
            <h2>{formatMoney(total)}</h2>
            <div className={`budget-meter ${delta < 0 ? "over" : ""}`}>
              <div><span>Ngân sách</span><strong>{formatCompactMoney(profile.budget)}</strong></div>
              <div className="meter-track"><i style={{ width: `${Math.min(100, (total / profile.budget) * 100)}%` }} /></div>
              <small>{delta >= 0 ? `Còn ${formatMoney(delta)}` : `Vượt ${formatMoney(Math.abs(delta))}`}</small>
            </div>
            <dl>
              <div><dt>Diện tích</dt><dd>{formatArea(areaM2(profile.widthCm, profile.depthCm))} m²</dd></div>
              <div><dt>Số cây</dt><dd>{plantCount(design)} cây</dd></div>
              <div><dt>Hạng mục</dt><dd>{design.length}/12</dd></div>
              <div><dt>Công chăm</dt><dd>{careMinutes(design)} phút/tuần</dd></div>
            </dl>
            {lastRemoved && <button className="undo-button" type="button" onClick={onUndo}><Undo2 aria-hidden="true" /> Hoàn tác lần bỏ gần nhất</button>}
            <button className="button button-primary button-block" type="button" onClick={onReview}>Xem dự toán & lịch chăm <ArrowRight aria-hidden="true" /></button>
            <small>Con số minh họa, chưa phải báo giá thi công.</small>
          </div>
          <div className="quick-tips">
            <CircleDollarSign aria-hidden="true" />
            <p><strong>Mẹo:</strong> chọn một vật thể trên sơ đồ để đổi vùng; trên mobile không cần kéo-thả.</p>
          </div>
        </aside>
      </div>
      <div className="mobile-budget-bar">
        <div><span>Dự toán</span><strong>{formatMoney(total)}</strong></div>
        <button type="button" onClick={onReview}>Xem chi tiết <ArrowRight aria-hidden="true" /></button>
      </div>
    </div>
  );
}
